import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getCostsByPeriod } from "@/services/apiCallsService";
import { useToast } from "@/hooks/use-toast";
import { isAuthenticated } from "@/services/tokenService";
import { usePermissions } from "@/hooks/usePermissions";

interface CostChartProps {
  clientId?: string;
  workspaceId?: string;
}

type ChartRow = {
  name: string;
  custoAtual: number;
  custoAnterior?: number;
};

export const CostChart = ({ clientId }: CostChartProps = {}) => {
  const [timeFilter, setTimeFilter] = useState<"1h" | "24h" | "7d" | "30d">(
    "24h",
  );
  const [showPreviousMonth, setShowPreviousMonth] = useState(false);
  const [chartData, setChartData] = useState<ChartRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { isAdminOrSupport } = usePermissions();

  useEffect(() => {
    if (!isAdminOrSupport) {
      setLoading(false);
      setChartData([]);
      setError(null);
      return;
    }
    if (isAuthenticated()) {
      loadChartData();
    } else {
      setLoading(false);
      setChartData([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, clientId, showPreviousMonth, isAdminOrSupport]);

  const resolveClientIdParam = (): string | undefined => {
    if (clientId === "null") return "null";
    if (clientId && clientId !== "all") return clientId;
    return undefined;
  };

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getCostsByPeriod({
        period: timeFilter,
        client_id: resolveClientIdParam(),
      });

      if (!response.success || !response.data) {
        const message =
          response.error?.message || "Erro ao carregar dados do gráfico";
        setError(message);
        setChartData([]);
        toast({
          title: "Erro",
          description: message,
          variant: "destructive",
        });
        return;
      }

      const formattedData: ChartRow[] = response.data.map((item) => ({
        name: item.name,
        custoAtual: item.custoAtual || 0,
        ...(showPreviousMonth
          ? { custoAnterior: item.custoAnterior || 0 }
          : {}),
      }));

      setChartData(formattedData);
    } catch {
      const message = "Erro ao carregar dados do gráfico";
      setError(message);
      setChartData([]);
      toast({
        title: "Erro",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAdminOrSupport) {
    return null;
  }

  const getDataAndTitle = () => {
    const costLabel = "Custo do provedor";

    switch (timeFilter) {
      case "1h":
        return {
          title: "Evolução de Custo (1h)",
          description: `${costLabel} nas últimas 1 hora`,
        };
      case "24h":
        return {
          title: "Evolução de Custo (24h)",
          description: `${costLabel} nas últimas 24 horas`,
        };
      case "7d":
        return {
          title: "Evolução de Custo (7 dias)",
          description: `${costLabel} nos últimos 7 dias`,
        };
      case "30d":
        return {
          title: "Evolução de Custo (30 dias)",
          description: `${costLabel} nos últimos 30 dias`,
        };
      default:
        return {
          title: "Evolução de Custo (24h)",
          description: `${costLabel} nas últimas 24 horas`,
        };
    }
  };

  const { title, description } = getDataAndTitle();

  const hasRealCost = chartData.some(
    (row) =>
      row.custoAtual > 0 ||
      (showPreviousMonth && (row.custoAnterior || 0) > 0),
  );

  return (
    <Card className="col-span-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="previous-month"
                checked={showPreviousMonth}
                onCheckedChange={(checked) =>
                  setShowPreviousMonth(checked === true)
                }
              />
              <label
                htmlFor="previous-month"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Período anterior
              </label>
            </div>
            <Select
              value={timeFilter}
              onValueChange={(value) =>
                setTimeFilter(value as "1h" | "24h" | "7d" | "30d")
              }
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">1 hora</SelectItem>
                <SelectItem value="24h">24 horas</SelectItem>
                <SelectItem value="7d">7 dias</SelectItem>
                <SelectItem value="30d">30 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-[400px]">
            <div className="text-sm text-muted-foreground">
              Carregando dados...
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-[400px]">
            <div className="text-sm text-destructive text-center px-4">
              Não foi possível carregar o custo real.
            </div>
          </div>
        ) : !hasRealCost ? (
          <div className="flex items-center justify-center h-[400px]">
            <div className="text-sm text-muted-foreground text-center px-4">
              Nenhum custo no período selecionado.
              <br />
              Custo: $0.00
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                formatter={(value, name) => [
                  `$${Number(value).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })}`,
                  name === "custoAtual"
                    ? "→ Período Atual"
                    : "Período Anterior",
                ]}
              />
              <Legend
                formatter={(value) =>
                  value === "custoAtual"
                    ? "→ Período Atual"
                    : "Período Anterior"
                }
                iconType="line"
                wrapperStyle={{
                  paddingTop: "20px",
                }}
              />
              <Line
                type="monotone"
                dataKey="custoAtual"
                stroke="hsl(var(--primary))"
                strokeWidth={3}
                dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
                activeDot={{
                  r: 6,
                  stroke: "hsl(var(--primary))",
                  strokeWidth: 2,
                }}
                name="custoAtual"
              />
              {showPreviousMonth && (
                <Line
                  type="monotone"
                  dataKey="custoAnterior"
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{
                    fill: "hsl(var(--muted-foreground))",
                    strokeWidth: 2,
                    r: 3,
                  }}
                  activeDot={{
                    r: 5,
                    stroke: "hsl(var(--muted-foreground))",
                    strokeWidth: 2,
                  }}
                  name="custoAnterior"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
